import { Box, HStack, Icon, IconButton, Text, VStack } from "@chakra-ui/react";
import type { ComponentProps, ReactNode, RefObject } from "react";
import { LuRefreshCw } from "react-icons/lu";

import { AdvisorAssistantMarkdown } from "./advisor-assistant-markdown";
import { ADVISOR_USER_BUBBLE_BG } from "./advisor-panel-tokens";
import {
  ADVISOR_MESSAGE_THREAD_GAP,
  ADVISOR_MESSAGE_THREAD_HORIZONTAL_PADDING,
} from "./advisor-side-panel";
import { formatUserBubbleAttachmentLine } from "./agent-ui-message-parts";
import {
  KinAgentMessageIcon,
  KinThinkingDots,
  KinThinkingIndicator,
} from "./kin-agent-icons";
import {
  KinAssistantTurnSlot,
  resolveKinAssistantTurnSlotKind,
} from "./kin-assistant-turn-slot";

export type AgentUserMessageBubbleProps = {
  text: string;
  attachmentFilenames?: readonly string[];
  bubbleBg?: string;
};

/**
 * Figma user question bubble ([13101-70065](https://www.figma.com/design/bYyIivHby22hQ1jTAxVnHV/Kin-Chatbot?node-id=13101-70065)).
 */
export function AgentUserMessageBubble({
  text,
  attachmentFilenames = [],
  bubbleBg = ADVISOR_USER_BUBBLE_BG,
}: AgentUserMessageBubbleProps) {
  const hasText = text.trim().length > 0;
  const attachLine = formatUserBubbleAttachmentLine(attachmentFilenames);

  return (
    <Box
      alignSelf="flex-end"
      px="10px"
      py="10px"
      borderTopLeftRadius="8px"
      borderBottomLeftRadius="8px"
      borderBottomRightRadius="8px"
      borderTopRightRadius={0}
      bg={bubbleBg}
      color="white"
    >
      {hasText ? (
        <Text fontSize="14px" lineHeight="20px" whiteSpace="pre-wrap">
          {text}
        </Text>
      ) : null}
      {attachLine ? (
        <Text
          fontSize="xs"
          opacity={0.92}
          mt={hasText ? 1 : 0}
          lineHeight="tall"
        >
          {attachLine}
        </Text>
      ) : null}
    </Box>
  );
}

export type AgentAssistantMessageBubbleProps = {
  text: string;
  footer?: ReactNode;
  progress?: ReactNode;
  /** Inline thinking dots when the turn is busy with tools but no progress lines or markdown yet. */
  thinkingDots?: boolean;
};

/** Assistant turn shell: Kin avatar + markdown bubble + optional footer (thumbs, follow-ups, etc.). */
export function AgentAssistantMessageBubble({
  text,
  footer,
  progress,
  thinkingDots = false,
}: AgentAssistantMessageBubbleProps) {
  const hasFooter = footer != null;
  const hasProgress = progress != null;
  const hasText = text.trim().length > 0;
  const showThinkingDots = thinkingDots && !hasText;
  const slotKind = resolveKinAssistantTurnSlotKind({
    hasText,
    hasProgress,
    thinkingDots: showThinkingDots,
  });
  const hasTurnBody = slotKind !== "none";

  return (
    <HStack align="start" gap={2} alignSelf="stretch" w="100%">
      <Box flexShrink={0} mt={0.5}>
        <KinAgentMessageIcon aria-hidden focusable={false} />
      </Box>
      <VStack
        align="stretch"
        gap={hasFooter || hasTurnBody ? 0.5 : 0}
        flex={1}
        minW={0}
        aria-busy={showThinkingDots ? true : undefined}
        aria-label={
          showThinkingDots ? "Assistant is preparing a reply" : undefined
        }
      >
        {hasTurnBody ? (
          <KinAssistantTurnSlot
            kind={slotKind}
            answer={hasText ? <AdvisorAssistantMarkdown text={text} /> : null}
            progress={progress ?? null}
            thinking={showThinkingDots ? <KinThinkingDots /> : null}
          />
        ) : null}
        {hasFooter ? footer : null}
      </VStack>
    </HStack>
  );
}

export type AgentSystemErrorMessageRowProps = {
  message: string;
  onRetry: () => void;
  showRetry: boolean;
};

export function AgentSystemErrorMessageRow({
  message,
  onRetry,
  showRetry,
}: AgentSystemErrorMessageRowProps) {
  return (
    <HStack align="start" gap={2} alignSelf="stretch" w="100%">
      <Box flexShrink={0} mt={0.5}>
        <KinAgentMessageIcon aria-hidden focusable={false} />
      </Box>
      <HStack
        px={3}
        py={2}
        borderRadius="lg"
        bg="red.50"
        borderWidth="1px"
        borderColor="red.100"
        gap={2}
        flex={1}
      >
        <Text fontSize="sm" color="red.700" flex={1}>
          {message}
        </Text>
        {showRetry ? (
          <IconButton
            variant="ghost"
            size="xs"
            aria-label="Retry"
            onClick={onRetry}
            color="red.600"
            flexShrink={0}
          >
            <Icon as={LuRefreshCw} boxSize={3.5} />
          </IconButton>
        ) : null}
      </HStack>
    </HStack>
  );
}

/** @alias {@link KinThinkingIndicator} — shared Kin “typing” row for org + partner shells. */
export const AgentThinkingIndicator = KinThinkingIndicator;

export type AgentMessageThreadScrollProps = ComponentProps<typeof Box> & {
  scrollRootRef?: RefObject<HTMLDivElement | null>;
  children: ReactNode;
};

/**
 * Scrollable transcript region with Figma thread inset + row gap ([13406-43053](https://www.figma.com/design/bYyIivHby22hQ1jTAxVnHV/Kin-Chatbot?node-id=13406-43053)).
 */
export function AgentMessageThreadScroll({
  scrollRootRef,
  children,
  ...rest
}: AgentMessageThreadScrollProps) {
  return (
    <Box
      ref={scrollRootRef}
      flex={1}
      minH={0}
      overflowY="auto"
      px={ADVISOR_MESSAGE_THREAD_HORIZONTAL_PADDING}
      py={3}
      bg="white"
      aria-live="polite"
      {...rest}
    >
      <VStack align="stretch" gap={ADVISOR_MESSAGE_THREAD_GAP}>
        {children}
      </VStack>
    </Box>
  );
}
